import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  theme,
  Badge,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import {
  getCheques,
  recolherCheques,
  depositarCheques,
  compensarCheques,
  devolverCheque,
  resgatarCheques,
  solicitarCheques,
} from "@/services/cheque";
import { getUnidades } from "@/services/unidade";
import { ChequeType, StatusCheque, StatusChequeLabel, StatusChequeCor } from "@/types/ChequeType";
import TrocaModal from "./Modals/trocaModal";
import ContatosDrawer from "./Modals/contatosDrawer";
import { usePermissions } from "@/hooks/usePermissions";

dayjs.locale("pt-br");

const { RangePicker } = DatePicker;

const STATUS_POR_ABA: Record<string, StatusCheque[]> = {
  recebimento: [StatusCheque.NaSecretaria, StatusCheque.Recolhido],
  deposito: [StatusCheque.Recolhido, StatusCheque.DevolvidoNoBanco, StatusCheque.DevolvidoNaSecretaria],
  depositados: [StatusCheque.Depositado],
  devolvidos: [StatusCheque.DevolvidoNoBanco, StatusCheque.DevolvidoNaSecretaria],
  solicitados: [StatusCheque.SolicitadoNaSecretaria],
  finalizados: [StatusCheque.Compensado, StatusCheque.TrocadoOutroPagamento],
};

const formatValor = (valor?: number) =>
  valor != null
    ? valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "-";

const formatData = (data?: string) => (data ? dayjs(data).format("DD/MM/YYYY") : "-");

type GrupoAluno = {
  key: string;
  alunoId: number;
  matricula?: number;
  unidadeNome?: string;
  nomeAluno?: string;
  nomeDonoCheque?: string;
  banco?: string;
  quantidade: number;
  valorTotal: number;
  cheques: ChequeType[];
};

export default function ConsultaCheque() {
  const { canRecebimentoFinanceiro } = usePermissions();
  const [abaAtiva, setAbaAtiva] = useState("recebimento");
  const [cheques, setCheques] = useState<ChequeType[]>([]);
  const [unidades, setUnidades] = useState<any[]>([]);
  const [unidadeIds, setUnidadeIds] = useState<number[]>([]);
  const [periodo, setPeriodo] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [busca, setBusca] = useState("");
  const [filtroRecebimento, setFiltroRecebimento] = useState<"todos" | "naoRecebidos" | "recebidos">("naoRecebidos");
  const [selecionados, setSelecionados] = useState<number[]>([]);

  const [depositoModalAberto, setDepositoModalAberto] = useState(false);
  const [depositoIds, setDepositoIds] = useState<number[]>([]);
  const [dataDeposito, setDataDeposito] = useState<Dayjs | null>(dayjs());

  const [devolverCh, setDevolverCh] = useState<ChequeType | null>(null);
  const [motivoDevolucao, setMotivoDevolucao] = useState("");

  const [trocaCheque, setTrocaCheque] = useState<ChequeType | null>(null);
  const [contatosChequeId, setContatosChequeId] = useState<number | null>(null);

  const carregarCheques = useCallback(async () => {
    try {
      const filtro = {
        status: STATUS_POR_ABA[abaAtiva],
        unidadeIds: unidadeIds.length > 0 ? unidadeIds : undefined,
        dataInicio: periodo?.[0] ? periodo[0].format("YYYY-MM-DD") : undefined,
        dataFim: periodo?.[1] ? periodo[1].format("YYYY-MM-DD") : undefined,
        busca: busca.trim() || undefined,
      };
      const { data } = await getCheques(filtro);
      setCheques(data ?? []);
      setSelecionados([]);
    } catch {
      message.error("Erro ao carregar os cheques.");
    }
  }, [abaAtiva, unidadeIds, periodo, busca]);

  useEffect(() => {
    carregarCheques();
  }, [carregarCheques]);

  useEffect(() => {
    getUnidades()
      .then(({ data }) => setUnidades(data ?? []))
      .catch(() => message.error("Erro ao carregar unidades."));
  }, []);

  const executar = async (acao: () => Promise<any>, sucesso: string) => {
    try {
      await acao();
      message.success(sucesso);
      carregarCheques();
    } catch (error: any) {
      message.error(error?.response?.data ?? "Erro ao executar a operação.");
    }
  };

  // ----------------------------------------------------------------- colunas
  const colunasBase: ColumnsType<ChequeType> = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: StatusCheque, record: ChequeType) => (
        <Tag color={StatusChequeCor[status]}>{record.statusLabel ?? StatusChequeLabel[status] ?? status}</Tag>
      ),
    },
    {
      title: "Data Recolhimento",
      dataIndex: "dataRecolhimento",
      key: "dataRecolhimento",
      render: (v: string) => formatData(v),
    },
    { title: "Matrícula", dataIndex: "matricula", key: "matricula" },
    { title: "Unidade", dataIndex: "unidadeNome", key: "unidadeNome" },
    { title: "Aluno", dataIndex: "nomeAluno", key: "nomeAluno" },
    { title: "Dono do Cheque", dataIndex: "nomeDonoCheque", key: "nomeDonoCheque" },
    { title: "Banco", dataIndex: "banco", key: "banco" },
    { title: "Nº Cheque", dataIndex: "numero", key: "numero" },
    {
      title: "Data",
      dataIndex: "dataVencimento",
      key: "dataVencimento",
      render: (v: string) => formatData(v),
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      render: (v: number) => formatValor(v),
    },
  ];

  // ------------------------------------------------------- aba 1: recebimento
  const chequesRecebimento = useMemo(() => {
    if (filtroRecebimento === "naoRecebidos")
      return cheques.filter((c) => c.status === StatusCheque.NaSecretaria);
    if (filtroRecebimento === "recebidos")
      return cheques.filter((c) => c.status === StatusCheque.Recolhido);
    return cheques;
  }, [cheques, filtroRecebimento]);

  const gruposAluno: GrupoAluno[] = useMemo(() => {
    const mapa = new Map<string, GrupoAluno>();
    chequesRecebimento.forEach((c) => {
      const chave = `${c.alunoId}-${c.nomeDonoCheque ?? ""}-${c.banco ?? ""}`;
      const grupo = mapa.get(chave) ?? {
        key: chave,
        alunoId: c.alunoId,
        matricula: c.matricula,
        unidadeNome: c.unidadeNome,
        nomeAluno: c.nomeAluno,
        nomeDonoCheque: c.nomeDonoCheque,
        banco: c.banco,
        quantidade: 0,
        valorTotal: 0,
        cheques: [],
      };
      grupo.quantidade += 1;
      grupo.valorTotal += c.valor ?? 0;
      grupo.cheques.push(c);
      mapa.set(chave, grupo);
    });
    return Array.from(mapa.values());
  }, [chequesRecebimento]);

  const colunasGrupo: ColumnsType<GrupoAluno> = [
    { title: "Matrícula", dataIndex: "matricula", key: "matricula" },
    { title: "Unidade", dataIndex: "unidadeNome", key: "unidadeNome" },
    { title: "Aluno", dataIndex: "nomeAluno", key: "nomeAluno" },
    { title: "Dono do Cheque", dataIndex: "nomeDonoCheque", key: "nomeDonoCheque" },
    { title: "Banco", dataIndex: "banco", key: "banco" },
    { title: "Qtd. Cheques", dataIndex: "quantidade", key: "quantidade" },
    {
      title: "Valor Total",
      dataIndex: "valorTotal",
      key: "valorTotal",
      render: (v: number) => formatValor(v),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, grupo) => {
        const pendentes = grupo.cheques.filter((c) => c.status === StatusCheque.NaSecretaria);
        if (pendentes.length === 0) return <Tag color="blue">Recebidos</Tag>;
        if (!canRecebimentoFinanceiro) return null;
        return (
          <Popconfirm
            title="Confirmar recebimento?"
            description={`Confirmar o recebimento de ${pendentes.length} cheque(s)?`}
            okText="Sim"
            cancelText="Não"
            onConfirm={() =>
              executar(
                () => recolherCheques(pendentes.map((c) => c.id)),
                "Recebimento confirmado com sucesso!"
              )
            }
          >
            <Button type="primary" size="small">
              Confirmar recebimento
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const colunasChequesDoGrupo: ColumnsType<ChequeType> = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: StatusCheque, record: ChequeType) => (
        <Tag color={StatusChequeCor[status]}>{record.statusLabel ?? StatusChequeLabel[status] ?? status}</Tag>
      ),
    },
    {
      title: "Data Recolhimento",
      dataIndex: "dataRecolhimento",
      key: "dataRecolhimento",
      render: (v: string) => formatData(v),
    },
    { title: "Nº Cheque", dataIndex: "numero", key: "numero" },
    { title: "Agência", dataIndex: "agencia", key: "agencia" },
    { title: "Conta", dataIndex: "conta", key: "conta" },
    {
      title: "Data",
      dataIndex: "dataVencimento",
      key: "dataVencimento",
      render: (v: string) => formatData(v),
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      render: (v: number) => formatValor(v),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, cheque) => (
        <Space size="small">
          {cheque.status === StatusCheque.NaSecretaria && canRecebimentoFinanceiro && (
            <Popconfirm
              title="Confirmar o recebimento deste cheque?"
              okText="Sim"
              cancelText="Não"
              onConfirm={() =>
                executar(() => recolherCheques([cheque.id]), "Recebimento confirmado com sucesso!")
              }
            >
              <Button type="link" size="small">
                Confirmar recebimento
              </Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="Solicitar cheque?"
            description="O cheque será marcado como Solicitado - na secretaria (não será depositado)."
            okText="Sim"
            cancelText="Não"
            onConfirm={() =>
              executar(() => solicitarCheques([cheque.id]), "Cheque solicitado com sucesso!")
            }
          >
            <Button type="link" size="small">
              Solicitar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ---------------------------------------------------------- aba 2: depósito
  const totalSelecionado = useMemo(() => {
    const itens = cheques.filter((c) => selecionados.includes(c.id));
    return {
      quantidade: itens.length,
      valor: itens.reduce((soma, c) => soma + (c.valor ?? 0), 0),
    };
  }, [cheques, selecionados]);

  const abrirDeposito = (ids: number[]) => {
    setDepositoIds(ids);
    setDataDeposito(dayjs());
    setDepositoModalAberto(true);
  };

  const confirmarDeposito = () => {
    if (!dataDeposito) {
      message.warning("Informe a data do depósito.");
      return;
    }
    setDepositoModalAberto(false);
    executar(
      () => depositarCheques(depositoIds, dataDeposito.format("YYYY-MM-DD")),
      "Depósito confirmado com sucesso!"
    );
  };

  // ------------------------------------------------------- aba 3: depositados
  const colunasDepositados: ColumnsType<ChequeType> = [
    ...colunasBase,
    {
      title: "Data Depósito",
      dataIndex: "dataDeposito",
      key: "dataDeposito",
      render: (v: string) => formatData(v),
    },
    {
      title: "Ação",
      key: "acao",
      render: (_, cheque) => (
        <Space size="small">
          <Popconfirm
            title="Confirmar compensação?"
            okText="Sim"
            cancelText="Não"
            onConfirm={() =>
              executar(() => compensarCheques([cheque.id]), "Cheque compensado com sucesso!")
            }
          >
            <Button type="link" size="small">
              Compensar
            </Button>
          </Popconfirm>
          <Button
            type="link"
            danger
            size="small"
            onClick={() => {
              setDevolverCh(cheque);
              setMotivoDevolucao("");
            }}
          >
            Devolver
          </Button>
        </Space>
      ),
    },
  ];

  // -------------------------------------------------------- aba 4: devolvidos
  const colunasDevolvidos: ColumnsType<ChequeType> = [
    ...colunasBase,
    { title: "Motivo Devolução", dataIndex: "motivoDevolucao", key: "motivoDevolucao" },
    {
      title: "Ação",
      key: "acao",
      render: (_, cheque) => (
        <Space size="small">
          {cheque.status === StatusCheque.DevolvidoNoBanco && (
            <Popconfirm
              title="Confirmar o resgate do cheque no banco?"
              okText="Sim"
              cancelText="Não"
              onConfirm={() =>
                executar(() => resgatarCheques([cheque.id]), "Cheque resgatado do banco!")
              }
            >
              <Button type="link" size="small">
                Resgatado do banco
              </Button>
            </Popconfirm>
          )}
          <Badge count={cheque.quantidadeContatos} size="small" offset={[-4, 2]}>
            <Button type="link" size="small" onClick={() => setContatosChequeId(cheque.id)}>
              Contatos
            </Button>
          </Badge>
          {canRecebimentoFinanceiro && (
            <Button type="link" size="small" onClick={() => abrirDeposito([cheque.id])}>
              Redepositar
            </Button>
          )}
          {cheque.status === StatusCheque.DevolvidoNaSecretaria && (
            <Button type="link" size="small" onClick={() => setTrocaCheque(cheque)}>
              Trocar pagamento
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // ------------------------------------------------------- aba 5: solicitados
  const colunasSolicitados: ColumnsType<ChequeType> = [
    ...colunasBase,
    {
      title: "Ação",
      key: "acao",
      render: (_, cheque) => (
        <Button type="link" size="small" onClick={() => setTrocaCheque(cheque)}>
          Trocar pagamento
        </Button>
      ),
    },
  ];

  // ------------------------------------------------------- aba 6: finalizados
  const colunasFinalizados: ColumnsType<ChequeType> = [
    ...colunasBase,
    { title: "Trocado por", dataIndex: "trocaPagamentoNome", key: "trocaPagamentoNome" },
    { title: "CV", dataIndex: "trocaCv", key: "trocaCv" },
    {
      title: "Data Transação",
      dataIndex: "trocaDataTransacao",
      key: "trocaDataTransacao",
      render: (v: string) => formatData(v),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selecionados,
    onChange: (keys: React.Key[]) => setSelecionados(keys as number[]),
  };

  const abas = [
    {
      key: "recebimento",
      label: "A Receber",
      children: (
        <>
          <div className="mb-4">
            <Radio.Group
              value={filtroRecebimento}
              onChange={(e) => setFiltroRecebimento(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              options={[
                { label: "⏳ Não recebidos", value: "naoRecebidos" },
                { label: "✅ Recebidos", value: "recebidos" },
                { label: "Todos", value: "todos" },
              ]}
            />
          </div>
          <Table
            columns={colunasGrupo}
            dataSource={gruposAluno}
            rowKey="key"
            expandable={{
              expandedRowRender: (grupo) => (
                <Table
                  columns={colunasChequesDoGrupo}
                  dataSource={grupo.cheques}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              ),
            }}
          />
        </>
      ),
    },
    {
      key: "deposito",
      label: "A Depositar",
      children: (
        <>
          <div className="mb-4 flex items-center gap-6">
            {canRecebimentoFinanceiro && (
              <Button
                type="primary"
                disabled={selecionados.length === 0}
                onClick={() => abrirDeposito(selecionados)}
              >
                Confirmar depósito
              </Button>
            )}
            <span className="text-white">
              {totalSelecionado.quantidade} cheque(s) selecionado(s) —{" "}
              <strong>{formatValor(totalSelecionado.valor)}</strong>
            </span>
          </div>
          <Table
            columns={colunasBase}
            dataSource={cheques}
            rowKey="id"
            rowSelection={rowSelection}
          />
        </>
      ),
    },
    {
      key: "depositados",
      label: "Depositados",
      children: (
        <>
          <div className="mb-4 flex items-center gap-6">
            <Popconfirm
              title="Confirmar compensação?"
              description={`Compensar ${selecionados.length} cheque(s)?`}
              okText="Sim"
              cancelText="Não"
              onConfirm={() =>
                executar(() => compensarCheques(selecionados), "Cheques compensados com sucesso!")
              }
            >
              <Button type="primary" disabled={selecionados.length === 0}>
                Compensar selecionados
              </Button>
            </Popconfirm>
          </div>
          <Table
            columns={colunasDepositados}
            dataSource={cheques}
            rowKey="id"
            rowSelection={rowSelection}
          />
        </>
      ),
    },
    {
      key: "devolvidos",
      label: "Devolvidos",
      children: (
        <Table columns={colunasDevolvidos} dataSource={cheques} rowKey="id" />
      ),
    },
    {
      key: "solicitados",
      label: "Solicitados",
      children: (
        <Table columns={colunasSolicitados} dataSource={cheques} rowKey="id" />
      ),
    },
    {
      key: "finalizados",
      label: "Finalizados",
      children: (
        <Table columns={colunasFinalizados} dataSource={cheques} rowKey="id" />
      ),
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Controle de Cheques</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-8 items-center mb-6">
          <Input
            allowClear
            placeholder="Buscar por nome, dono do cheque ou CPF"
            className="w-80"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Select
            allowClear
            mode="multiple"
            placeholder="Todas as unidades"
            className="w-64"
            value={unidadeIds}
            onChange={(value) => setUnidadeIds(value)}
            options={unidades.map((u: any) => ({ value: u.id, label: u.nome }))}
          />
          <RangePicker
            format="DD/MM/YYYY"
            value={periodo}
            onChange={(datas) => setPeriodo(datas as [Dayjs | null, Dayjs | null] | null)}
            placeholder={["Data inicial", "Data final"]}
          />
        </div>

        <Tabs
          activeKey={abaAtiva}
          onChange={(key) => {
            setAbaAtiva(key);
            setSelecionados([]);
          }}
          items={abas}
        />

        {/* Modal de depósito */}
        <Modal
          title="Confirmar Depósito"
          open={depositoModalAberto}
          onOk={confirmarDeposito}
          onCancel={() => setDepositoModalAberto(false)}
          okText="Confirmar"
          cancelText="Cancelar"
        >
          <p className="mb-2">
            {depositoIds.length} cheque(s) será(ão) marcado(s) como Depositado.
          </p>
          <Form layout="vertical">
            <Form.Item label="Data do depósito" required>
              <DatePicker
                format="DD/MM/YYYY"
                value={dataDeposito}
                onChange={(data) => setDataDeposito(data)}
                className="w-full"
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal de devolução */}
        <Modal
          title={`Devolver Cheque nº ${devolverCh?.numero ?? ""}`}
          open={devolverCh !== null}
          onOk={() => {
            if (!motivoDevolucao.trim()) {
              message.warning("Informe o motivo da devolução.");
              return;
            }
            const id = devolverCh!.id;
            setDevolverCh(null);
            executar(() => devolverCheque(id, motivoDevolucao), "Devolução registrada!");
          }}
          onCancel={() => setDevolverCh(null)}
          okText="Confirmar"
          cancelText="Cancelar"
        >
          <Form layout="vertical">
            <Form.Item label="Motivo da devolução" required>
              <Input.TextArea
                rows={3}
                maxLength={200}
                value={motivoDevolucao}
                onChange={(e) => setMotivoDevolucao(e.target.value)}
                placeholder="Ex.: sem fundos (alínea 11)"
              />
            </Form.Item>
          </Form>
        </Modal>

        <TrocaModal
          cheque={trocaCheque}
          onClose={() => setTrocaCheque(null)}
          onDone={() => {
            setTrocaCheque(null);
            carregarCheques();
          }}
        />

        <ContatosDrawer
          chequeId={contatosChequeId}
          onClose={() => setContatosChequeId(null)}
          onChanged={carregarCheques}
        />
      </ConfigProvider>
    </>
  );
}
